<template>
    <div class="homepage">
        <!-- Hero Section -->
        <section class="hero_section vh-100">
            <div class="hero_content">
                <Presence>
                  <Motion 
                    :initial="{ opacity: 0, x: -20 }"
                    :animate="{ opacity: 0.3, x: 0 }"
                    :transition="{ duration: 0.8, delay: 0.2 }"
                    class="hero_desc text-center text-md-end"
                  >
                    Welcome
                  </Motion>
                </Presence>
                
                <Presence>
                  <Motion 
                    :initial="{ opacity: 0, scale: 0.9, y: 20 }"
                    :animate="{ opacity: 1, scale: 1, y: 0 }"
                    :transition="{ duration: 1, easing: [0.16, 1, 0.3, 1] }"
                    class="hero_title mega_text"
                  >
                    tensbase
                  </Motion>
                </Presence>
                
                <Presence>
                  <Motion 
                    :initial="{ opacity: 0, x: 20 }"
                    :animate="{ opacity: 0.3, x: 0 }"
                    :transition="{ duration: 0.8, delay: 0.2 }"
                    class="hero_desc text-center text-md-start"
                  >
                    Est. 2026
                  </Motion>
                </Presence>
            </div>
            
            <Motion 
              :initial="{ opacity: 0, y: 10 }"
              :animate="{ opacity: 0.5, y: 0 }"
              :transition="{ duration: 0.8, delay: 0.8 }"
              class="hero_tagline"
            >
              <p>The Foundation of a Smarter World</p>
            </Motion>

            <Motion 
              :initial="{ opacity: 0 }"
              :animate="{ opacity: 1 }"
              :transition="{ delay: 1.2 }"
              class="scroll_indicator"
            >
              <div class="line"></div>
            </Motion>
        </section>

        <!-- Services Section -->
        <section class="services_section py-32">
          <div class="container">
            <div class="section_header">
              <Motion 
                :initial="{ opacity: 0 }"
                :in-view="{ opacity: 0.5 }"
                class="section_label"
              >
                Our Expertise
              </Motion>
              <Motion 
                :initial="{ opacity: 0, y: 20 }"
                :in-view="{ opacity: 1, y: 0 }"
                :transition="{ duration: 0.8 }"
                class="section_title"
              >
                Specialized Services
              </Motion>
            </div>

            <div class="services_grid">
              <!-- Service 1 -->
              <Motion 
                :initial="{ opacity: 0, y: 30 }"
                :in-view="{ opacity: 1, y: 0 }"
                :transition="{ duration: 0.6, delay: 0.1 }"
                class="service_card"
              >
                <div class="service_icon">
                  <Monitor class="icon" />
                </div>
                <h3>Custom Web Development</h3>
                <p>High-performance, scalable web solutions tailored to your business objectives. We build digital experiences that are fast, secure, and conversion-optimized.</p>
              </Motion>

              <!-- Service 2 -->
              <Motion 
                :initial="{ opacity: 0, y: 30 }"
                :in-view="{ opacity: 1, y: 0 }"
                :transition="{ duration: 0.6, delay: 0.2 }"
                class="service_card"
              >
                <div class="service_icon">
                  <Smartphone class="icon" />
                </div>
                <h3>Mobile App Development</h3>
                <p>Native and cross-platform mobile ecosystems designed for seamless user engagement. We transform complex ideas into intuitive, high-performance mobile interfaces.</p>
              </Motion>

              <!-- Service 3 -->
              <Motion 
                :initial="{ opacity: 0, y: 30 }"
                :in-view="{ opacity: 1, y: 0 }"
                :transition="{ duration: 0.6, delay: 0.3 }"
                class="service_card"
              >
                <div class="service_icon">
                  <ShoppingBag class="icon" />
                </div>
                <h3>Shopify Apps</h3>
                <p>Extend your e-commerce capabilities with custom Shopify applications. We build powerful tools that automate workflows and enhance your store's functionality.</p>
              </Motion>
            </div>
          </div>
        </section>

        <!-- CTA Section -->
        <section class="cta_section py-48">
          <div class="container text-center">
            <Motion 
              :initial="{ opacity: 0, scale: 0.95 }"
              :in-view="{ opacity: 1, scale: 1 }"
              :transition="{ duration: 0.8 }"
              class="cta_title"
            >
              Build your proprietary base.
            </Motion>
            <Motion
              :hover="{ scale: 1.05 }"
              :press="{ scale: 0.95 }"
            >
              <router-link to="/book" class="cta_btn">
                Book a Discovery
              </router-link>
            </Motion>
          </div>
        </section>

        <!-- Footer -->
        <footer class="footer">
          <div class="container">
            <div class="footer_content">
              <div class="footer_left">
                <span class="copyright">© 2026 Tensbase Malaysia</span>
              </div>
            </div>
          </div>
        </footer>
    </div>
</template>

<script setup>
import { Motion, Presence } from '@motionone/vue';
import { 
  Monitor, 
  Smartphone, 
  ShoppingBag 
} from 'lucide-vue-next';
</script>

<style lang="scss" scoped>
.vh-100 { height: 100vh; }
.py-32 { padding: 8rem 0; }
.py-48 { padding: 12rem 0; }

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.hero_section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  .hero_content {
    display: flex;
    align-items: center;
    gap: 2rem;
    
    .hero_desc {
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.4em;
      opacity: 0.3;
      width: 120px;
    }
    
    .hero_title {
      margin: 0;
      line-height: 0.8;
    }
  }
  
  .hero_tagline {
    margin-top: 3rem;
    font-size: 0.9rem;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    opacity: 0.5;
  }

  .scroll_indicator {
    position: absolute;
    bottom: 3rem;
    left: 50%;
    transform: translateX(-50%);
    
    .line {
      width: 1px;
      height: 60px;
      background: linear-gradient(to bottom, transparent, rgba(255,255,255,0.3), transparent);
    }
  }
}

.section_header {
  margin-bottom: 5rem;
  text-align: center;
}

.section_label {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.3em;
  margin-bottom: 1rem;
}

.section_title {
  font-size: 4rem;
  font-weight: 300;
  letter-spacing: -0.02em;
}

.services_grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
}

.service_card {
  padding: 2.5rem 2rem;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 2rem;
  transition: border-color 0.3s ease;
  
  &:hover {
    border-color: rgba(255,255,255,0.2);
  }
  
  .service_icon {
    width: 3rem;
    height: 3rem;
    margin-bottom: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255,255,255,0.05);
    border-radius: 1rem;
    
    .icon {
      width: 1.5rem;
      height: 1.5rem;
      opacity: 0.6;
    }
  }
  
  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1.2rem;
    letter-spacing: 0.02em;
    min-height: 3.6rem;
    display: flex;
    align-items: flex-start;
  }
  
  p {
    font-size: 1rem;
    line-height: 1.7;
    opacity: 0.4;
  }
}

.cta_section {
  background: #ffffff;
  color: #000000;
}

.cta_title {
  font-size: 5rem;
  font-weight: 300;
  margin-bottom: 4rem;
  letter-spacing: -0.03em;
  color: #000000;
}

.cta_btn {
  display: inline-block;
  padding: 1.5rem 4rem;
  background: #000000;
  color: #ffffff;
  text-decoration: none;
  border-radius: 999px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-size: 0.8rem;
}

.footer {
  padding: 4rem 0;
  border-top: 1px solid rgba(255,255,255,0.05);
  
  .footer_content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    opacity: 0.3;
  }
}

@media (max-width: 1024px) {
  .services_grid { grid-template-columns: 1fr; }
  .section_title { font-size: 3rem; }
  .cta_title { font-size: 3rem; }
  .footer { padding: 1.2rem 0; }
}

@media (max-width: 768px) {
  .hero_content { 
    flex-direction: column; 
    gap: 1rem;
    .hero_desc { width: auto; }
  }
}
</style>
